import React from 'react'
import { graphql, useStaticQuery } from 'gatsby'

import SEO from '../components/seo'
import NewsSectionBlock from '../components/NewsSectionBlock'
import Typography from '../components/Common/Typography'

import BannerSection from '../components/BannerSection'
import List from '../components/Common/List'
import Carousel from '../components/Carousel'
import { TImageStaticQuery } from '../types/pages'
import { GlobalContext } from '../context/GlobalContext'
import Link from '../components/Common/Link'

const ExposingIraqiTreasurePart1 = () => {
  const carouselData0 = [
    {
      imageId: 'carouselData0image1',
      alt: 'Part-1-1',
    },
    {
      imageId: 'carouselData0image2',
      alt: 'Part-1-2',
    },
    {
      imageId: 'carouselData0image3',
      alt: 'Part-1-3',
    },
    {
      imageId: 'carouselData0image4',
      alt: 'Part-1-4',
    },
  ]

  const carouselData1 = [
    {
      imageId: 'carouselData1image1',
      alt: 'Defining-the-project’s-purpose-1',
    },
    {
      imageId: 'carouselData1image2',
      alt: 'Defining-the-project’s-purpose-2',
    },
  ]

  const carouselData2 = [
    {
      imageId: 'carouselData2image1',
      alt: 'International-collaboration-in-planning-1',
    },
    {
      imageId: 'carouselData2image2',
      alt: 'International-collaboration-in-planning-2',
    },
    {
      imageId: 'carouselData2image3',
      alt: 'International-collaboration-in-planning-3',
    },
    {
      imageId: 'carouselData2image4',
      alt: 'International-collaboration-in-planning-4',
    },
  ]

  const carouselData3 = [
    {
      imageId: 'carouselData3image1',
      alt: 'Streamlined-Collaboration-with-Digital-Notes-&-Workflows-1',
    },
    {
      imageId: 'carouselData3image2',
      alt: 'Streamlined-Collaboration-with-Digital-Notes-&-Workflows-1-2',
    },
  ]

  const { isDesktop } = React.useContext(GlobalContext)
  const images = useStaticQuery(imagesQuery) as TImageStaticQuery

  return (
    <>
      <SEO
        title="Through the Sands of Time: SIGNAX Exposes an Iraqi Treasure - Part 1"
        description="SIGNAX drives the planning of Tell Dehaila’s archaeological expedition with advanced drone surveys, collaborating with Russian, German, and Iraqi experts. Non-invasive tech preserves the 3500-year-old site, refining satellite imagery for precise mapping. Explore how SIGNAX sets the stage for historic discoveries!"
      />
      <BannerSection
        imageDesktop={images.banner.childImageSharp.gatsbyImageData}
        imageMobile={images.banner_mobile.childImageSharp.gatsbyImageData}
        title={`Through the Sands of\u00a0Time:\nSIGNAX Exposes an\u00a0Iraqi\nTreasure – Part 1`}
        titleSize={isDesktop ? 60 : 40}
        colorShadow="dark"
        imgStyle={{ height: '467px' }}
      />
      <NewsSectionBlock date="April 22, 2025">
        <Typography variant="h3" className="mt-0">
          Part 1
        </Typography>
        <Typography variant="body1">
          The Iraqi-Russian Multidisciplinary Project, led by&nbsp;the Institute
          of&nbsp;Oriental Studies and the Institute of&nbsp;Archaeology
          of&nbsp;the Russian Academy of&nbsp;Sciences, aims to&nbsp;uncover the
          secrets of&nbsp;an&nbsp;ancient Mesopotamian region of&nbsp;the Eridu
          plain in&nbsp;Southern Iraq and its important ancient city,{' '}
          <Link
            href="https://en.wikipedia.org/wiki/Tell_Dehaila"
            rel="nofollow noreferrer"
            target="_blank"
          >
            Tell&nbsp;Dehaila
          </Link>
          , spanning more than 4000 years of&nbsp;history. Situated
          in&nbsp;a&nbsp;remote region with a&nbsp;landscape scarred
          by&nbsp;looters and subtle features buried beneath the surface, the
          project demands meticulous planning and cutting-edge technology
          to&nbsp;succeed. SIGNAX, a&nbsp;leader in&nbsp;geospatial solutions,
          plays an&nbsp;indispensable role during this phase, collaborating with
          an&nbsp;international team of&nbsp;experts to&nbsp;preserve the site’s
          fragile remains and analyze its historical data with exceptional
          precision, setting a&nbsp;new benchmark for non-invasive
          archaeological exploration.
        </Typography>

        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData0}
          centeredSlides={true}
        />
        <Typography variant="h3">Defining the project’s purpose</Typography>

        <Typography variant="body1">
          Modern archaeology isn’t that much about digging. Excavation
          is&nbsp;needed to&nbsp;discover written documents and artefacts,
          to&nbsp;obtain materials for dating, to&nbsp;clarify archaeological
          structures and to&nbsp;understand the stratigraphy, the vertical
          composition of&nbsp;the superposing construction levels. This work
          is&nbsp;very time-consuming and it&nbsp;cannot be&nbsp;done over large
          areas. To&nbsp;get meaningful results, archaeologists need
          to&nbsp;carefully select the right places to&nbsp;be&nbsp;excavated.
          Non-destructive methods are widely used for that. Also, they allow
          to&nbsp;document the site in&nbsp;its entirety, something that
          is&nbsp;never attained by&nbsp;classical earth-moving archaeology.
          In&nbsp;fact, when a&nbsp;site has been excavated, it&nbsp;always
          means that only a&nbsp;small part of&nbsp;it&nbsp;became known. The
          modern non-destructive methods, combined with excavation at&nbsp;the
          final stage, allow a&nbsp;thorough exploration of&nbsp;the ancient
          settlement with minimal damage.
        </Typography>
        <Typography variant="body1">
          To do this, archaeologists start with freely available satellite
          pictures, such as&nbsp;declassified{' '}
          <Link
            href="https://corona.cast.uark.edu/"
            rel="nofollow noreferrer"
            target="_blank"
          >
            CORONA
          </Link>{' '}
          images of&nbsp;the 1960-ies and 1970-ies, but also the more recent
          digital elevation maps from Shuttle Radar Topography and{' '}
          <Link
            href="https://en.wikipedia.org/wiki/Copernicus_Programme"
            rel="nofollow noreferrer"
            target="_blank"
          >
            COPERNICUS
          </Link>{' '}
          project, and other available satellite images. But they are only
          sufficient for the initial planning and general research purposes over
          large areas. To&nbsp;investigate an&nbsp;archaeological site, detailed
          images with good georeferencing are needed, and this is&nbsp;where
          SIGNAX’s expertise in&nbsp;drone surveying comes&nbsp;in. SIGNAX
          contributes its specialized capabilities&nbsp;to:
        </Typography>

        <List>
          <li>
            <span style={{ fontWeight: 500 }}>
              Mapping and interpreting a&nbsp;site,
            </span>{' '}
            the specific scope of&nbsp;SIGNAX’s work, supports the broader
            archaeological goals of&nbsp;understanding the site’s role
            in&nbsp;Mesopotamian history, refining the coarse satellite data
            with high-resolution imagery
          </li>
          <li>
            <span style={{ fontWeight: 500 }}>
              Preserving the site’s integrity,
            </span>{' '}
            non-destructive methods allow to&nbsp;learn a&nbsp;lot without
            further disturbance to&nbsp;the cultural deposits already damaged
            by&nbsp;looting and by&nbsp;natural erosion, contributing
            to&nbsp;the protection of&nbsp;the historical heritage
          </li>
          <li>
            <span style={{ fontWeight: 500 }}>Uncovering hidden history,</span>{' '}
            targeting subtle traces of&nbsp;irrigation systems, urban layouts,
            and defensive structures dating back millennia, offering insights
            into ancient life that the satellite imagery can only hint&nbsp;at.
          </li>
        </List>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData1}
          centeredSlides={true}
          autoHeight
        />
        <Typography variant="body1">
          This purpose establishes the archaeological mission at&nbsp;Tell
          Dehaila as&nbsp;a&nbsp;pioneering effort, merging traditional
          archaeological approaches with cutting-edge geospatial tools
          to&nbsp;unlock Tell Dehaila’s long-buried story.
        </Typography>

        <Typography variant="h3">
          International collaboration in planning
        </Typography>
        <Typography variant="body1">
          The archaeological project unites a&nbsp;diverse team
          of&nbsp;specialists from the United Arab Emirates, Russia, Germany,
          and Iraq, fostering a&nbsp;rich exchange of&nbsp;knowledge and skills.
          Archaeologists from Novgorod State University and
          Ludwig-Maximilians-Universität München worked alongside
          representatives from Iraq’s State Board of&nbsp;Antiquities and
          Heritage (SBAH), ensuring the project respects cultural and regulatory
          frameworks. SIGNAX integrates its technical proficiency into this
          international coalition, enhancing the planning process with:
        </Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>
              Detailed survey area planning,
            </span>
            {` `}planning four key zones: a&nbsp;70m-altitude central tell area
            scanned at&nbsp;high resolution, and three 160m-altitude regions
            (North-Western Canal, Protective Trench, and Irrigation Area), each
            designed to&nbsp;capture critical site features with a&nbsp;200-600m
            marker spacing.
          </li>
          <li>
            <span style={{ fontWeight: 500 }}>
              Support geophysical mapping,
            </span>
            {` `}preparing a&nbsp;precise 40m x&nbsp;40m grid with
            90&nbsp;ground control points to&nbsp;help geophysicists align their
            measurements of&nbsp;the magnetic field with aerial data for
            a&nbsp;comprehensive subsurface analysis.
          </li>
        </List>

        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData2}
          centeredSlides={true}
        />
        <Typography variant="body1">
          This collaborative planning phase, enriched by&nbsp;SIGNAX’s input,
          lays a&nbsp;robust foundation for the fieldwork ahead, balancing
          scientific precision with the practical realities of&nbsp;the site’s
          remote location and environmental constraints.
        </Typography>
        <Typography variant="h3">
          SIGNAX’s role in preserving and analyzing historical data
        </Typography>
        <Typography variant="body1">
          SIGNAX’s drone surveying technology enables the team to&nbsp;capture
          and preserve historical data despite environmental challenges. The
          site’s harsh weather conditions — scorching heat with no&nbsp;shade
          or&nbsp;fresh water sources — would test the team’s endurance, while
          insects, solifuges (camel spiders), snakes, and wolves pose constant
          risks during fieldwork. Limited road access, tight flight windows, and
          unpredictable weather further complicate operations. Using the modern
          quadcopter drone, equipped with high-resolution imaging sensors and
          an&nbsp;RTK module, and flight planning software, SIGNAX executes
          a&nbsp;drone survey operation. SIGNAX ensures:
        </Typography>
        <List>
          <li>
            <span style={{ fontWeight: 500 }}>
              Non-invasive data collection,
            </span>
            {` `}using ground control points across the site;
          </li>
          <li>
            <span style={{ fontWeight: 500 }}>Multi-condition analysis,</span>
            {` `}scheduling flights over the central site three times — morning,
            after rain, and before sunset — to reveal varying light angles and
            soil moisture levels, enhancing visibility of&nbsp;faint features
            like ancient canals and buildings.
          </li>
        </List>
        <Typography variant="body1">
          SIGNAX’s efforts focus on&nbsp;creating detailed maps and models
          to&nbsp;support the mission’s goals. By&nbsp;capturing data across
          multiple conditions and preparing it&nbsp;for analysis, SIGNAX
          provides the team with a&nbsp;clear view of&nbsp;Tell Dehaila’s past,
          ensuring that the site’s historical narrative remains intact and
          accessible for future exploration. This work equips the expedition
          with the tools to&nbsp;interpret the site’s significance without
          disrupting its fragile remains.
        </Typography>
        <Carousel
          className="mt-4"
          images={images}
          carouselData={carouselData3}
          centeredSlides={true}
        />
        <Typography variant="h3">Setting the stage for discovery</Typography>
        <Typography variant="body1">
          The planning phase of&nbsp;the investigations at&nbsp;Tell Dehail,
          bolstered by&nbsp;SIGNAX’s innovative contributions, establishes
          a&nbsp;model for modern archaeological exploration. By&nbsp;uniting
          international expertise and leveraging drone technology, the team aims
          to&nbsp;overcome the site’s logistical and environmental challenges
          to&nbsp;pursue a&nbsp;shared vision: understanding a&nbsp;civilization
          that thrived up&nbsp;to&nbsp;4000 years ago. This groundwork protects
          the world heritage and enables discoveries in&nbsp;the upcoming
          excavation phase, showcasing the power of&nbsp;technology
          to&nbsp;connect the present with the ancient past.
        </Typography>
      </NewsSectionBlock>
    </>
  )
}

const imagesQuery = graphql`
  query {
    banner: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/banner.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    banner_mobile: file(
      relativePath: {
        eq: "news/exposing-iraqi-treasure-part1/banner-mobile.png"
      }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    carouselData0image1: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider0-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData0image2: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider0-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData0image3: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider0-3.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData0image4: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider0-4.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData1image1: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider1-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData1image2: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider1-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image1: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider2-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image2: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider2-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image3: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider2-3.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData2image4: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider2-4.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData3image1: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider3-1.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
    carouselData3image2: file(
      relativePath: { eq: "news/exposing-iraqi-treasure-part1/slider3-2.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(width: 782, placeholder: BLURRED)
      }
    }
  }
`

export default ExposingIraqiTreasurePart1
